import React, { useEffect, useState } from 'react'
import {
  makeStyles,
  Grid,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Radio,
  Box,
  Typography,
  IconButton,
  Tooltip as MuiTooptip,
  withStyles,
} from '@material-ui/core'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'reducers'
import {
  setOpenMCARS080,
} from 'reducers/entrySearchHistoryReducer'
import { magiContants } from 'utils/contants'
import { getMessage } from 'common/messageUtil'
import { isEmpty } from 'common/generalUtil'
import { search, setSearchCondition } from 'reducers/applicantListReducer'
import { LoadingDialog } from 'components'

const useStyles = makeStyles(theme => ({
  buttonGroup: {
    marginTop: '8px',
    marginBottom: '16px',
    display: 'flex',
    justifyContent: 'center',
  },
  cancelButton: {
    '&:hover': {
      backgroundColor: '#c1d1eb',
      borderColor: '#999999',
    },
    backgroundColor: '#e0e0e0',
    border: '1px solid',
    borderColor: '#cbcbcb',
    borderRadius: '4px',
    color: '#1a1a1a',
    minWidth: 150,
  },
  cancelButtonContainer: {
    justifyContent: 'end',
    marginRight: '30px !important',
  },
  confirmButton: {
    '&:hover': {
      backgroundColor: '#4596e6',
    },
    backgroundColor: '#3380cc',
    color: (theme.palette as any).white,
    minWidth: 150,
  },
  confirmButtonContainer: {
    justifyContent: 'start',
    marginLeft: '30px !important',
  },
  dialog: {
     padding: 0, 
     minWidth: '960px', 
     maxHeight: '100%',
    '& .MuiDialog-paperScrollPaper': {
      display: window.navigator.userAgent
        .toLocaleLowerCase()
        .match(/(trident)\/([\d.]+)/)
        ? 'block'
        : 'flex',
      overflowY: window.navigator.userAgent
        .toLocaleLowerCase()
        .match(/(trident)\/([\d.]+)/)
        ? 'auto'
        : 'hidden',
    },
  },
  explain: {
    display: 'flex',
    width: '94%',
    fontSize: '14px',
    marginLeft: '1%',
    marginTop: '1%',
  },
  explainContainer: {
    display:'flex',
    height: '55px',
  },
  helpButton: {
    padding: 0,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  helpButtonContainer: {
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
    width: '18%',
    marginTop: '1%',
  },
  radioContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  searchHistoryContainer: {
    marginLeft: '1%',
    marginTop: '1%',
    display: 'flex',
    flexDirection: 'column',
  },
  searchHistoryContents: {
    display: 'flex',
    marginTop: '5px',
    marginBottom: '5px',
  },
  searchConditionDetailBox: {
    border: '1px solid black',
    width: '90%',
    maxHeight: '222px',
    overflow: 'auto',
    padding: '10px',
    wordWrap: 'break-word',
  },
  dialogTitle: {
    backgroundColor: '#133e80',
  },
  subTitle: {
    color: (theme.palette as any).white,
    fontSize: '18px',
    fontWeight: 900,
  },
}))

const Tooltip = withStyles({
  tooltip: {
    fontSize: '1em',
    maxWidth: '500px',
  },
  popper: {
    whiteSpace: 'pre-line'
  }
})(MuiTooptip)

type SelectingSearchHistory = 'recent' | 'previous';

const MCARS080 = () => {
  const classes = useStyles()
  const dispatch = useDispatch()

  /** OK／キャンセルするボタン 活性フラグ */
  const [isEnableSubmitFlag, setIsEnableSubmitFlag] = useState<boolean>(false)
  const [isEnableCancelFlag, setIsEnableCancelFlag] = useState<boolean>(true)
  /** 選択中の検索条件 recent または previous */
  const [selectingSearchHistory, setSelectingSearchHistory] = useState<SelectingSearchHistory>('recent')

  const searchRequest = useSelector((state: RootState) => state.applicantList.searchRequest)

  const {
    openMCARS080,
    searchHistoryInfo,
  } = useSelector((state: RootState) => state.entrySearchHistory)

  useEffect(() => {
    // 応募者一覧遷移時にモーダルが開いたままの場合、モーダルを閉じる
    if (openMCARS080) {
      dispatch(setOpenMCARS080(false))
    }
  },[])

  useEffect(() => {
    // モーダル展開時
    if (openMCARS080) {
      // ラジオボタンの選択状態をリセット
      if (searchHistoryInfo.previousEntrySearchHistory) {
        setSelectingSearchHistory('previous')
      } else {
        setSelectingSearchHistory('recent')
      } 
      // 「キャンセルする」ボタンを活性化
      setIsEnableCancelFlag(true)
    }
  },[openMCARS080])

  useEffect(() => {
    // 検索履歴が1件以上ある場合、OKボタンを活性化
    if (searchHistoryInfo && !isEmpty(searchHistoryInfo.recentEntrySearchHistory)) {
      setIsEnableSubmitFlag(true)
    }
  },[searchHistoryInfo])

  // OKボタン押下時
  const handleSubmit = () => {
    // 「OK」「キャンセルする」ボタンを非活性
    setIsEnableSubmitFlag(false)
    setIsEnableCancelFlag(false)
    // 選択した検索履歴の条件で検索を実施
    let selectingSearchCondition: string
    if (selectingSearchHistory === 'recent') {
      selectingSearchCondition = searchHistoryInfo.recentEntrySearchHistory
    } else {
      selectingSearchCondition = searchHistoryInfo.previousEntrySearchHistory
    }

    dispatch(setSearchCondition(JSON.parse(selectingSearchCondition)))

    dispatch(
      search({
        request: {
          ...searchRequest,
          searchCondition: selectingSearchCondition,
          searchCount: 1,
          sourceFunctionId: 'MCARS080',
        },
        isInitFilter: true,
      })
    )
  } 

  const handleCancel = () => {
    dispatch(setOpenMCARS080(false))
  }

  return (
    <Dialog
      fullWidth
      maxWidth='md'
      open={openMCARS080}
      className={`${classes.dialog} mainDialog searchHistoryDialog`}>
      <LoadingDialog className='overlay' />
      <DialogTitle className={classes.dialogTitle} id='max-width-dialog-title'>
        <Box className={classes.subTitle}>
          検索履歴を指定
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box className={classes.explainContainer}>
          <Typography className={classes.explain}>
            最近の2回の検索履歴から、詳細な検索条件を指定できます。
            <br />
            以下から選択し、「OK」ボタンをクリックしてください。
          </Typography>
          <Box className={classes.helpButtonContainer}>
            <Tooltip title={getMessage(magiContants.MESSAGECODE_MCARS080_001)} placement='top'>
              <IconButton className={classes.helpButton}>
                <HelpOutlineIcon fontSize='large' htmlColor='black'/>
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {searchHistoryInfo ? (
          <Box className={classes.searchHistoryContainer}>
            {searchHistoryInfo.previousEntrySearchHistory ? (
              <Box 
                className={classes.searchHistoryContents} 
                onClick={() => setSelectingSearchHistory('previous')}
              >
                <Box className={classes.radioContainer}>
                  <Radio
                    color='primary'
                    checked={selectingSearchHistory === 'previous'}
                    value={'previousEntrySearchHistory'}
                  />
                </Box>
                <Box
                  className={classes.searchConditionDetailBox}
                  tabIndex={-1}
                  dangerouslySetInnerHTML={{__html: searchHistoryInfo.previousEntrySearchHistoryDisplay}}
                />
              </Box>
            ) : null}
            {searchHistoryInfo.recentEntrySearchHistory ? (
              <Box 
                className={classes.searchHistoryContents} 
                onClick={() => setSelectingSearchHistory('recent')}
              >
                <Box className={classes.radioContainer}>
                  <Radio
                    color='primary'
                    checked={selectingSearchHistory === 'recent'}
                    value={'recentEntrySearchHistory'}
                  />
                </Box>
                <Box
                  className={classes.searchConditionDetailBox}
                  tabIndex={-1}
                  dangerouslySetInnerHTML={{__html: searchHistoryInfo.recentEntrySearchHistoryDisplay}}
                />
              </Box>
            ) : (
              <Typography>
                過去の検索履歴はありません。
              </Typography>
            )}
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions className={classes.buttonGroup}>
        {/* キャンセルボタン */}
        <Grid
          container
          direction='row'
          className={classes.cancelButtonContainer}>
          <Button
            variant='contained'
            className={classes.cancelButton}
            disabled={!isEnableCancelFlag}
            onClick={handleCancel}>
            キャンセルする
          </Button>
        </Grid>
        {/* OKボタン */}
        <Grid
          container
          direction='row'
          className={classes.confirmButtonContainer}>
          <Button
            data-gtm-click= {magiContants.GTM_MCARS080_001}
            variant='contained'
            className={classes.confirmButton}
            disabled={!isEnableSubmitFlag}
            onClick={handleSubmit}
            onKeyDown={event => {
              if (event.repeat) {
                event.preventDefault()
              }
            }}>
            OK
          </Button>
        </Grid>
      </DialogActions>
    </Dialog>
  )
}
export default MCARS080
